import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'designSystem/components/Tabs'
import { Callout } from 'features/sentinelDesign/designStyles'
import {
  SENTINEL_STAGES,
  type SentinelStageId,
  STAGE_LABELS,
} from 'features/sentinelDesign/stages'
import { CasesPanel } from 'features/sentinels/components/CasesPanel'
import { DesignRunOverviewSections } from 'features/sentinels/components/DesignRunOverviewSections'
import { MonitorTab } from 'features/sentinels/components/MonitorTab'
import type {
  SentinelDesignRunResultFile,
  SentinelRecordIdentity,
} from 'features/sentinels/recordTypes'
import { contextIdFromEntryId } from 'features/sentinels/runState'
import { Subtitle, Title } from 'features/sentinels/styles'
import type { SentinelEntryData } from 'features/sentinels/types'
import { FC, useEffect, useState } from 'react'
import styled from 'styled-components'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  color: ${({ theme }) => theme.colors.grayscale['100']};
  font-size: 0.8125rem;

  /*
   * The panels hold tables and auto-fill grids; a flex item's automatic minimum
   * size is its content, which would push those past the pane instead of
   * letting them shrink.
   */
  > * {
    min-width: 0;
  }
`

const TabPane = styled(TabsContent)`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 0.9rem;

  > * {
    min-width: 0;
  }
`

/** The four stages, in order, as continue-from entry points. */
const STAGE_ENTRIES = SENTINEL_STAGES

const ContinueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-top: 0.6rem;
`

const ContinueLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.grayscale['400']};
  margin-right: 0.1rem;
`

const short = (tag: string | undefined) =>
  tag ? tag.split('-').slice(1).join('-') || tag : '—'

export interface DesignRunDetailsProps {
  record: SentinelDesignRunResultFile & SentinelRecordIdentity
  entryId?: string
  /** The run entry's file pointers, for the artifacts stored beside the fit. */
  files?: SentinelEntryData['files']
  /**
   * Reopen the staged design flow on this run's context, optionally at a
   * specific stage. Editing the plan and refitting overwrites this run in place
   * (the run entry is keyed on the context). Given the 16-hex context id derived
   * from the run's context entry, plus the stage to land on.
   */
  onReRun?: (contextId: string, stage?: SentinelStageId) => void
  /** Land on this tab when the detail opens (default 'overview'). */
  initialTab?: 'overview' | 'monitor'
  /** Skip the inner name heading when the host page already renders one. */
  hideTitle?: boolean
  /**
   * A case to reveal on open — from clicking its bubble on the chart. Forces
   * the Monitor tab and expands the case in the Cases panel. The `nonce` makes
   * a repeat click on the same case re-fire.
   */
  focusCase?: { caseId: string; nonce: number } | null
}

/**
 * A finished agentic-design run.
 *
 * Rendered through the design flow's own panels rather than a bespoke view: the
 * run's `result.json` IS the fitter's `TrainResponse`, which is what those
 * panels already read, so what a saved run shows is what the flow showed while
 * it was fitting. The legacy `SentinelDetails` target renderer wanted a
 * different contract — daily median series, a cleaning report, a resolved
 * agent design — that no backend has produced since the design flow replaced
 * the old pipeline.
 */
export const DesignRunDetails: FC<DesignRunDetailsProps> = ({
  record,
  entryId,
  files,
  onReRun,
  initialTab = 'overview',
  hideTitle = false,
  focusCase = null,
}) => {
  const { t } = useLingui()

  const methods = record.methods ?? {}
  const drivers = record.drivers ?? record.plan?.drivers ?? []
  const methodLabels = Object.fromEntries(
    Object.entries(methods).map(([key, method]) => [key, method.label || key])
  )

  // Controlled so opening a case from the Live check chart can jump to Cases.
  const [tab, setTab] = useState<string>(initialTab)
  // Bumped when a case is opened so the Cases panel re-fetches.
  const [caseRefresh, setCaseRefresh] = useState(0)

  // A case-bubble click (focusCase) forces the Monitor tab so its Cases panel,
  // which expands the clicked case, is visible.
  useEffect(() => {
    if (focusCase) setTab('monitor')
  }, [focusCase])

  // The context this run was planned from — continuing resumes the design flow
  // on it. Editing and refitting overwrites this run in place.
  const contextId = contextIdFromEntryId(record.context?.entry_id)

  return (
    <Wrap>
      <div>
        {!hideTitle && <Title>{record.name}</Title>}
        <Subtitle>
          {record.site_id}
          <Trans> · target </Trans>
          {short(record.target)} · {drivers.length}{' '}
          <Trans>drivers · created </Trans>
          {new Date(record.created_at).toLocaleString()}
        </Subtitle>
        {onReRun && contextId && (
          <ContinueRow>
            <ContinueLabel>
              <Trans>Continue from</Trans>
            </ContinueLabel>
            {STAGE_ENTRIES.map((stage) => (
              <Button
                key={stage}
                variant='outline'
                onClick={() => onReRun(contextId, stage)}
                title={t`Reopen the design flow at the ${stage} stage. Refitting overwrites this run.`}>
                {STAGE_LABELS[stage]}
              </Button>
            ))}
          </ContinueRow>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value='overview'>
            <Trans>Overview</Trans>
          </TabsTrigger>
          <TabsTrigger value='monitor'>
            <Trans>Monitor</Trans>
          </TabsTrigger>
        </TabsList>

        <TabPane value='overview'>
          <DesignRunOverviewSections
            record={record}
            entryId={entryId}
            files={files}
            onCaseOpened={() => {
              setCaseRefresh((n) => n + 1)
              setTab('monitor')
            }}
          />
        </TabPane>

        <TabPane value='monitor'>
          {entryId ? (
            <>
              <MonitorTab
                entryId={entryId}
                site={record.site_id}
                target={record.target}
                methodKeys={Object.keys(methods)}
                methodLabels={methodLabels}
              />
              <CasesPanel
                entryId={entryId}
                site={record.site_id}
                refreshKey={caseRefresh}
                focusCase={focusCase}
              />
            </>
          ) : (
            <Callout $tone='warning'>
              <Trans>
                This run has no entry yet, so it cannot be monitored.
              </Trans>
            </Callout>
          )}
        </TabPane>
      </Tabs>
    </Wrap>
  )
}
